import { Button, ConfirmModal, Table, Loader, openNotification } from 'tt-ui-lib/core';
import { AddIcon, DownIcon, EditIcon, LitterBinIcon } from 'tt-ui-lib/icons';
import styles from '../ManualTab.module.scss';
import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material';
import moment from 'moment/moment';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  GET_ADMIN_TUTORIALS,
  DELETE_TUTORIAL,
  DELETE_SECTION,
  CREATE_SECTION,
  UPDATE_SECTION,
} from '../../../../../../../api';
import { useEffect, useState } from 'react';
import AddOrEditTutorial from './AddOrEditTutorial';
import AddOrEditSectionModal from '../AddOrEditSectionModal';

const CustomizedAccordion = styled(Accordion)(() => ({
  '&.MuiAccordion-root': {
    borderRadius: '16px',
    width: '100%',
    padding: '10px',
    margin: 0,
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.04)',
    '&:before': {
      display: 'none',
    },
  },
  '.MuiAccordionDetails-root': {
    padding: 0,
  },
  '.MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    paddingRight: '24px',
    maxWidth: '-webkit-fill-available',
  },
  boxShadow: '0px 4px 24px 0px #0000000A',
}));

const TutorialsTab = () => {
  const [pageLoading, setPageLoading] = useState(false);
  const [tutorials, setTutorials] = useState([]);
  const [tutorialAction, setTutorialAction] = useState('');
  const [tutorialsCount, setTutorialsCount] = useState(null);
  const [activeSection, setActiveSection] = useState(false);
  const [openSectionModal, setOpenSectionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [getAllTutorials] = useLazyQuery(GET_ADMIN_TUTORIALS, {
    fetchPolicy: 'network-only',
  });
  const [deleteTutorial] = useMutation(DELETE_TUTORIAL);
  const [deleteSection] = useMutation(DELETE_SECTION);
  const [createSection] = useMutation(CREATE_SECTION, {
    variables: { input: {} },
    fetchPolicy: 'network-only',
  });
  const [updateSection] = useMutation(UPDATE_SECTION, {
    variables: { input: {} },
    fetchPolicy: 'network-only',
  });

  const handleDeleteTutorial = async () => {
    setLoadingAction(true);
    setShowDeleteModal(false);
    const res = await deleteTutorial({ variables: { id: showDeleteModal } });
    if (res.data && res.data.deleteTutorial && res.data.deleteTutorial.success) {
      setTutorials((prevState) =>
        prevState.map((group) => ({
          ...group,
          tutorials: group.tutorials.filter((tutorial) => tutorial.id !== showDeleteModal),
        }))
      );
    }
    setLoadingAction(false);
  };

  const handleDeleteSection = async () => {
    setLoadingAction(true);
    setShowDeleteSectionModal(false);
    const res = await deleteSection({ variables: { id: showDeleteSectionModal } });
    if (res.data && res.data.deleteSection && res.data.deleteSection.success) {
      setTutorials((prevState) =>
        prevState.filter((group) => group.section.id !== showDeleteSectionModal)
      );
      openNotification({
        message: 'Section deleted successfully',
        type: 'success',
      });
    }
    setLoadingAction(false);
  };

  const columns = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Document name',
      align: 'left',
      width: 130,
    },
    {
      key: 'type',
      dataIndex: 'type',
      title: 'File',
      align: 'left',
      width: 150,
    },
    {
      key: 'created_at',
      dataIndex: 'created_at',
      title: 'Creation date',
      align: 'left',
      width: 100,
      render: (value, _) => (value ? moment(value).format('DD.MM.YYYY') : '---'),
    },
    {
      key: 'updated_at',
      dataIndex: 'updated_at',
      title: 'Edit Date',
      align: 'left',
      width: 100,
      render: (value, _) => (value ? moment(value).format('DD.MM.YYYY') : '---'),
    },
    {
      dataIndex: 'actions',
      align: 'left',
      width: 100,
      render: (_, val) => (
        <div className={styles.tableActions}>
          <Button className={styles.actionBtn} onClick={() => setTutorialAction(val.id)}>
            <EditIcon style={{ width: 24, height: 24 }} />
          </Button>
          <Button className={styles.actionBtn} onClick={() => setShowDeleteModal(val.id)}>
            <LitterBinIcon style={{ width: 24, height: 24 }} />
          </Button>
        </div>
      ),
    },
  ];

  const getTutorials = async () => {
    setPageLoading(true);
    const response = await getAllTutorials({ variables: { type: 'tutorial' } });
    if (response && response.data && response.data.showAllAdminTutorial.data) {
      setTutorials(response.data.showAllAdminTutorial.data);
      setTutorialsCount(response.data.showAllAdminTutorial.totalCount);
    }
    setPageLoading(false);
  };

  useEffect(() => {
    (async () => {
      await getTutorials();
    })();
  }, [tutorialAction]);

  const addTutorial = (section) => {
    setTutorialAction('add');
    setActiveSection(section);
  };

  const handleSubmitSectionModal = async (newSection) => {
    if (nameError) {
      openNotification({
        message: 'Title exceeds the maximum allowed length of 50 characters.',
        type: 'error',
      });
      return;
    }
    try {
      setLoadingAction(true);
      let response;
      let notificationMessage;
      let updatedTutorials;

      if (activeSection?.id) {
        response = await updateSection({
          variables: {
            input: {
              id: activeSection.id,
              name: newSection,
            },
          },
        });

        if (response?.data?.updateSection) {
          updatedTutorials = tutorials.map((group) =>
            group.section.id === response.data.updateSection.id
              ? { ...group, section: response.data.updateSection }
              : group
          );
          notificationMessage = 'Section updated successfully';
        } else {
          throw new Error('Update failed');
        }
      } else {
        response = await createSection({
          variables: {
            input: {
              name: newSection,
              type: 'tutorial',
            },
          },
        });

        if (response?.data?.createSection) {
          updatedTutorials = [...tutorials, { section: response.data.createSection }];
          notificationMessage = 'Section created successfully';
        } else {
          throw new Error('Creation failed');
        }
      }

      setTutorials(updatedTutorials);
      openNotification({
        message: notificationMessage,
        type: 'success',
      });
    } catch (error) {
      openNotification({
        message: 'Something went wrong, try again',
        type: 'error',
      });
    } finally {
      setOpenSectionModal(false);
      setLoadingAction(false);
    }
  };

  const handleEditSectionModal = (section) => {
    setActiveSection(section);
    setOpenSectionModal(true);
  };

  return (
    <>
      {pageLoading ? (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <>
          {!tutorialAction ? (
            <div className={styles.tabContent}>
              {tutorials.map((item) => (
                <CustomizedAccordion>
                  <AccordionSummary
                    expandIcon={<DownIcon style={{ width: 24, height: 24 }} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <div className={styles.accordionTitle}>{item?.section?.name}</div>
                    <div className={styles.accordionActions}>
                      <Button
                        className={styles.actionBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSectionModal(item.section);
                        }}
                      >
                        <EditIcon style={{ width: 24, height: 24 }} />
                      </Button>
                      <Button
                        className={styles.actionBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteSectionModal(item?.section?.id);
                          setActiveSection(item.section);
                        }}
                      >
                        <LitterBinIcon style={{ width: 24, height: 24 }} />
                      </Button>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className={styles.accordionDetails}>
                      <Table columns={columns} data={item.tutorials} scroll={{ x: 1300 }} />
                      <Button
                        type="mainButton"
                        className={styles.addBtn}
                        onClick={() => addTutorial(item.section)}
                      >
                        <AddIcon style={{ width: 24, height: 24 }} />
                        <div className={styles.btnText}>Add new tutorial</div>
                      </Button>
                    </div>
                  </AccordionDetails>
                </CustomizedAccordion>
              ))}
              <Button
                type="mainButton"
                className={styles.addBtn}
                onClick={() => {
                  setActiveSection(null);
                  setOpenSectionModal(true);
                }}
              >
                <AddIcon style={{ width: 24, height: 24 }} />
                <div className={styles.btnText}>Add new Section</div>
              </Button>
            </div>
          ) : (
            <AddOrEditTutorial
              tutorialActionOrId={tutorialAction}
              activeSection={activeSection}
              setTutorialAction={setTutorialAction}
              tutorialsCount={tutorialsCount}
            />
          )}
          <AddOrEditSectionModal
            openSectionModal={openSectionModal}
            setOpenSectionModal={setOpenSectionModal}
            activeSection={activeSection}
            loadingAction={loadingAction}
            handleSubmit={handleSubmitSectionModal}
            setActiveSection={setActiveSection}
            error={nameError}
            setError={setNameError}
          />
          <ConfirmModal
            open={showDeleteModal}
            title="Do you really want to remove the tutorial?"
            okText="Delete"
            loading={loadingAction}
            onConfirm={() => handleDeleteTutorial()}
            onClose={() => setShowDeleteModal(false)}
          />
          <ConfirmModal
            open={showDeleteSectionModal}
            title={`Delete ${activeSection?.name}`}
            subTitle="You really want to delete this section. All entries inside will also be deleted"
            okText="Delete"
            loading={loadingAction}
            onConfirm={() => handleDeleteSection()}
            onClose={() => setShowDeleteSectionModal(false)}
          />
        </>
      )}
    </>
  );
};
export default TutorialsTab;
